/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getTokenExpirySeconds } from 'modules/common/helpers/jwt';
import { selectRefreshToken } from 'modules/common/auth/selectors';
import store from 'store/index';
import { API } from 'modules/dashboard/constants';
import { selectTokenRevocation } from 'modules/dashboard/selectors';
import request from 'modules/common/utils/request';

/**
 * Based on the token expiration check the token expired or not
 * @returns
 */
const useIsAuthenticated = () => {
  /* Determine if authorized, from context or however you're doing it */
  const refreshToken = useSelector(selectRefreshToken);
  //
  const isAuthenticated = useCallback(() => {
    if (refreshToken) {
      const exp = getTokenExpirySeconds(refreshToken);
      const isTokenRevoked = selectTokenRevocation(store.getState());
      if (isTokenRevoked) {
        return false;
      }
      return exp !== null && exp >= Date.now() / 1000;
    }
    return false;
  }, [refreshToken]);
  /**
   * Checks token activity on the provider; returns false when the
   * introspection call fails or the token is reported inactive
   */
  const checkTokenActivity = useCallback(async () => {
    if (refreshToken) {
      try {
        const data = await request(API.POST_TOKEN_INFO, { token: refreshToken }, false);
        return data?.active;
      } catch (e) {
        return false;
      }
    }
    return false;
  }, [refreshToken]);

  const checkAuthentication = useCallback((candidateRefreshToken) => {
    if (candidateRefreshToken) {
      const exp = getTokenExpirySeconds(candidateRefreshToken);
      return exp !== null && exp >= Date.now() / 1000;
    }
    return false;
  }, []);
  //
  return { isAuthenticated, checkAuthentication, checkTokenActivity };
};
//
export default useIsAuthenticated;
